import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Input, Kbd } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Context } from "@/App";
import { RoutePath, SPACING } from "@/generic/config";
import { useNavSections } from "@/generic/navigation";
import { useScopes } from "@/model/Token";
import { DialogBackdrop, DialogContent, DialogRoot } from "@/components/ui/dialog";
import Icon, { Icons } from "./Icons";
import Text from "./Text";

const OPEN_EVENT = "oms:open-command-palette";

/** Opens the palette from anywhere (e.g. the header's search button). */
export const openCommandPalette = () => window.dispatchEvent(new Event(OPEN_EVENT));

export const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

type Command = { id: string; title: string; group: string; icon: IconDefinition; path: string; keywords: string };

/** Lowercase and strip accents so "kund" finds "Kunder" and "oversikt" finds "Översikt". */
const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Ctrl+K / ⌘K: search every menu page (and a few "new …" shortcuts) and jump
 * there with the keyboard. Built from the same list as the sidebar, so it only
 * offers what the user's role can open.
 */
const CommandPalette = () => {
    const { translations: t } = useContext(Context);
    const sections = useNavSections();
    const scope = useScopes();
    const isAdmin = scope.includes("ADMIN") || scope.includes("SUPERUSER");
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const commands: Command[] = useMemo(() => {
        const pages = sections.flatMap(section => section.items.map(item => ({
            id: item.path,
            title: item.title,
            group: section.title ?? t.commandPalette.pages,
            icon: item.icon,
            path: item.path,
            keywords: `${item.title} ${section.title ?? ""} ${item.path}`,
        })));
        const create = isAdmin ? [
            { id: "new-order", title: t.commandPalette.newOrder, group: t.commandPalette.create, icon: Icons.Order, path: `${RoutePath.Orders}/new`, keywords: "new ny order create skapa" },
            { id: "new-customer", title: t.commandPalette.newCustomer, group: t.commandPalette.create, icon: Icons.Customer, path: `${RoutePath.Customers}/new`, keywords: "new ny kund customer create skapa" },
            { id: "new-worksite", title: t.commandPalette.newWorksite, group: t.commandPalette.create, icon: Icons.Worksite, path: `${RoutePath.Worksites}/new`, keywords: "new ny arbetsplats worksite create skapa" },
        ] : [];
        return [...pages, ...create];
    }, [sections, isAdmin, t]);

    const results = useMemo(() => {
        const words = norm(query).split(/\s+/).filter(Boolean);
        if (words.length === 0) return commands;
        return commands.filter(c => {
            const hay = norm(c.keywords);
            return words.every(w => hay.includes(w));
        });
    }, [commands, query]);

    // Global shortcut + open event.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen(o => !o);
            }
        };
        const onOpen = () => setOpen(true);
        window.addEventListener("keydown", onKey);
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener(OPEN_EVENT, onOpen);
        };
    }, []);

    useEffect(() => {
        if (open) {
            setQuery("");
            setActive(0);
        }
    }, [open]);
    useEffect(() => setActive(0), [query]);

    // Keep the highlighted row in view.
    useEffect(() => {
        listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [active]);

    const go = (command: Command | undefined) => {
        if (!command) return;
        setOpen(false);
        navigate(command.path);
    };

    const onInputKey = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive(i => Math.min(results.length - 1, i + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive(i => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(results[active]);
        }
    };

    let lastGroup = "";
    return (
        <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)} placement="top" size="md" motionPreset="slide-in-top">
            <DialogBackdrop />
            <DialogContent mt="12vh" p={0} overflow="hidden" data-testid="command-palette">
                <Flex alignItems="center" gap={SPACING * 1.5} px={SPACING * 2} borderBottomWidth="1px">
                    <Box color="fg.muted"><Icon icon={Icons.Search} /></Box>
                    <Input
                        autoFocus
                        variant="flushed"
                        border="none"
                        size="lg"
                        placeholder={t.commandPalette.placeholder}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onInputKey}
                        aria-label={t.commandPalette.placeholder}
                        data-testid="command-palette-input"
                        _focusVisible={{ boxShadow: "none" }}
                    />
                    <Kbd size="sm">Esc</Kbd>
                </Flex>
                <Box ref={listRef} maxH="50vh" overflowY="auto" py={SPACING} role="listbox">
                    {results.length === 0 && (
                        <Text px={SPACING * 2} py={SPACING * 2} color="fg.muted">{t.commandPalette.noResults}</Text>
                    )}
                    {results.map((c, i) => {
                        const header = c.group !== lastGroup ? c.group : null;
                        lastGroup = c.group;
                        const isActive = i === active;
                        return (
                            <React.Fragment key={c.id}>
                                {header && (
                                    <Text px={SPACING * 2} pt={SPACING} pb={SPACING / 2} fontSize="x-small" fontWeight="700"
                                        textTransform="uppercase" letterSpacing="0.08em" color="fg.muted">
                                        {header}
                                    </Text>
                                )}
                                <Flex
                                    role="option"
                                    aria-selected={isActive}
                                    data-index={i}
                                    alignItems="center"
                                    gap={SPACING * 1.5}
                                    mx={SPACING}
                                    px={SPACING}
                                    py={SPACING * 0.75}
                                    borderRadius="md"
                                    cursor="pointer"
                                    bg={isActive ? "bg.muted" : undefined}
                                    onMouseMove={() => setActive(i)}
                                    onClick={() => go(c)}
                                >
                                    <Box w={5} textAlign="center" color="fg.muted"><Icon icon={c.icon} /></Box>
                                    <Text flex={1} fontSize="sm">{c.title}</Text>
                                    {isActive && <Box color="fg.muted" fontSize="xs"><Icon icon={Icons.arrowRight} /></Box>}
                                </Flex>
                            </React.Fragment>
                        );
                    })}
                </Box>
                <Flex gap={SPACING * 2} px={SPACING * 2} py={SPACING} borderTopWidth="1px" color="fg.muted" fontSize="xs">
                    <Flex gap={1} alignItems="center"><Kbd size="sm">↑</Kbd><Kbd size="sm">↓</Kbd> {t.commandPalette.move}</Flex>
                    <Flex gap={1} alignItems="center"><Kbd size="sm">Enter</Kbd> {t.commandPalette.open}</Flex>
                    <Flex gap={1} alignItems="center"><Kbd size="sm">{isMac() ? "⌘" : "Ctrl"}</Kbd><Kbd size="sm">K</Kbd> {t.commandPalette.toggle}</Flex>
                </Flex>
            </DialogContent>
        </DialogRoot>
    );
};

export default CommandPalette;
